import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import TextField from "@/common/components/TextField";
import { ProductData } from "@/common/types/product.type";
import { formatCurrency, formatDecimalUnit } from "@/common/utils/formatters";
import { FileBox } from "lucide-react";
import { ClientCompanyAllowedCategoryAndProductData } from '../../types/allowedCategoriesAndProducts.type';
import { CompanyBranchProductGrantedCreditData } from "../../types/companyBranches.type";
import { ControlledByType } from "../../types/companyGovernmentAllocation.type";

interface CreditProductConfigProps {
  product: ProductData;
  productIndex: number;
  grantedCredit: string | number;
  grantedQuantity?: string | number;
  allowedProductSummary?: ClientCompanyAllowedCategoryAndProductData;
  productsGrantedCredits: CompanyBranchProductGrantedCreditData[];
  productCategoryGrantedCredit: number;
  companyKind?: string;
  controlledBy?: ControlledByType;
}

export const CreditProductConfig = ({
  product,
  productIndex,
  grantedCredit,
  grantedQuantity,
  allowedProductSummary,
  productsGrantedCredits,
  productCategoryGrantedCredit,
  companyKind,
  controlledBy,
}: CreditProductConfigProps) => {
  const isQuantity = controlledBy === 'quantity';
  const unitForQuantity = product.unit_for_quantity;

  const productGrantedCreditsSum = productsGrantedCredits?.reduce((sum, product, index) => {
    if (index !== productIndex) {
      if (isQuantity) {
        return sum + (Number(product.granted_quantity) || 0);
      }
      return sum + (Number(product.granted_credit) || 0);
    }
    return sum;
  }, 0) || 0;

  const allowedProductAvailableTotal = isQuantity
    ? (Number(allowedProductSummary?.available_quantity) || 0)
    : (Number(allowedProductSummary?.available_total) || 0);
  const productCategoryGrantedCreditLeft = productCategoryGrantedCredit - productGrantedCreditsSum;

  const currentValue = isQuantity ? Number(grantedQuantity || 0) : Number(grantedCredit || 0);
  const availableTotal = allowedProductAvailableTotal
    ? Math.min(allowedProductAvailableTotal, Math.max(productCategoryGrantedCreditLeft, 0))
    : allowedProductAvailableTotal || Math.max(productCategoryGrantedCreditLeft, 0);
  const availableForAllocation = Math.max(productCategoryGrantedCredit - currentValue, 0);

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={FileBox}
        text={product?.display_name || ""}
        className="flex-row-reverse overflow-hidden !p-0 gap-0"
        contentClassName="px-2 py-1"
      />

      <div className='flex flex-col gap-1'>
        <TextField
          label={isQuantity ? "Quantidade total" : "Valor total"}
          value={isQuantity ? formatDecimalUnit(grantedQuantity || 0, unitForQuantity) : formatCurrency(Number(grantedCredit))}
          disabled
        />

        <div className='flex gap-3'>
          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade possível: ' : 'Total possível: '}
            {isQuantity ? formatDecimalUnit(availableTotal || 0, unitForQuantity) : formatCurrency(availableTotal || 0)}
          </p>

          <p className='italic text-xs text-white-400'>
            Disponível para distribuir: {isQuantity ? formatDecimalUnit(availableForAllocation, unitForQuantity) : formatCurrency(availableForAllocation)}
          </p>

          {companyKind !== 'private_company' && (
            <p className='italic text-xs text-white-400'>
              {isQuantity ? 'Quantidade alocada: ' : 'Total alocado: '}
              {isQuantity
                ? (allowedProductSummary?.allocated_quantity ? formatDecimalUnit(allowedProductSummary?.allocated_quantity, unitForQuantity) : '--')
                : (allowedProductSummary?.allocated_total ? formatCurrency(Number(allowedProductSummary?.allocated_total) || 0) : '--')
              }
            </p>
          )}

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade distribuída: ' : 'Total distribuído: '}
            {isQuantity
              ? (allowedProductSummary?.distributed_quantity ? formatDecimalUnit(allowedProductSummary?.distributed_quantity, unitForQuantity) : '--')
              : (allowedProductSummary?.distributed_total ? formatCurrency(Number(allowedProductSummary?.distributed_total) || 0) : '--')
            }
          </p>
        </div>
      </div>
    </Box>
  )
}
